import React, { useContext, useEffect, useState } from "react";
import cn from "classnames";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useSharedTranslations } from "../../lib/i18n";
import { useTranslation } from "react-i18next";
import { IValidateError, platformContext } from "../../lib";
import styles from "./radiogroup.module.scss";
import { Platform } from "@yandex-int/hr-components/lib";

export interface RadiogroupOptions {
    value: string;
    label: string;
    disabled?: boolean;
}

interface IRadiogroupProps {
    className?: string;
    name?: string;
    title?: string;
    value?: string | null;
    options: RadiogroupOptions[];
    onChange: (value: string) => void;
    vertical?: boolean;
    disabled?: boolean;
    validate?: boolean;
    validateFunc?: (value?: string | null) => IValidateError;
    onValidate?: (error: IValidateError) => void;
    triggerValidate?: number;
}

export const Radiogroup: React.FC<IRadiogroupProps> = React.memo(({
                                                                      className,
                                                                      name,
                                                                      title,
                                                                      value,
                                                                      options,
                                                                      onChange,
                                                                      vertical = true,
                                                                      disabled,
                                                                      validate,
                                                                      validateFunc,
                                                                      onValidate,
                                                                      triggerValidate
                                                                  }) => {
    const t = useSharedTranslations();
    const { i18n } = useTranslation();
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();
    const platform = useContext(platformContext);

    useEffect(() => {
        if (triggerValidate && validate) {
            validateValue(value);
        } else {
            setIsError(false);
            setValidationError(undefined);
        }
    }, [triggerValidate, validate, validateFunc, value]);

    useEffect(() => {
        if (isError) {
            validateValue(value);
        }
    }, [i18n.language]);

    const onChangeRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
        const changeVal = e.target.value;
        value = changeVal;

        if (validate) {
            validateValue(changeVal);
        }

        onChange && onChange(changeVal);
    };

    const validateValue = (val: string | undefined | null) => {
        let error = false;
        let message: string | undefined = undefined;
        if (!validateFunc) {
            if (!val) {
                error = true;
                message = t("requiredFieldError");
            }
        } else {
            const validation = validateFunc(val);
            error = !validation.valid;
            message = validation.message;
        }
        setIsError(error);
        setValidationError(message);
        onValidate && onValidate({ valid: !error, message: message });
    };

    return <div className={className}>
        {title && <div className={styles.title}>{title}</div>}
        <RadioGroup
            className={vertical ? styles.vertical : (platform === Platform.DESKTOP ? styles.horizontal : styles.horizontalMobile)}
            name={name}
            value={value}
        >
            {options.map((option) =>
                <FormControlLabel key={option.value}
                                  value={option.value}
                                  className={styles.label}
                                  control={
                                      <Radio
                                          sx={{
                                              color: disabled || option.disabled ? "var(--hr-color-text-tertiary) !important" : "#64469E !important"
                                          }}
                                          onChange={onChangeRadio}
                                          disabled={disabled || option.disabled}
                                      />
                                  }
                                  label={option.label}
                />
            )}
        </RadioGroup>
        {isError && validationError && <div className="Textinput_state_error">
            <div className={cn(styles.validationError, "Textinput-Hint")}>
                {validationError}
            </div>
        </div>}
    </div>;

});
